import React, { useEffect, useState } from 'react';
import { toast, Loading } from '@cotera/client/app/components';
import { useWhoami } from '@cotera/client/app/stores/org';
import { useAuth } from '@workos-inc/authkit-react';
import { useSlackBotInstallation } from '../components/tool-connections/use-slack-bot-installation';
import { PROVIDER_ICONS } from '../components/tool-connections/tool-connections-dialog';
import { triggerSlackBotInstall } from '../components/tool-connections/credential-strategy-views/slack-oauth-install-popout';
import { emitToolCredentialUpdated } from '@cotera/client/app/etc/emit-tool-credential-updated';
import { useTenantedClient } from '@cotera/client/app/stores/org';
import { Assert } from '@cotera/utilities';
import { Button } from '../components/ui/button';
import { Icon } from '@cotera/client/app/components/ui/icon';
import { InlineDeleteConfirmation } from '../components/tool-connections/credential-connection-cards';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';

const SLACK_PROVIDER = 'slack';

const SlackIcon =
  PROVIDER_ICONS[SLACK_PROVIDER as keyof typeof PROVIDER_ICONS] ?? (() => null);

export const SlackConnectionSection: React.FC = () => {
  const orgId = useWhoami((s) => s.org.id);
  const client = useTenantedClient();
  const { user } = useAuth();
  const { installed, teamId } = useSlackBotInstallation();
  const [isInstalling, setIsInstalling] = useState(false);
  const [showDisconnectConfirm, setShowDisconnectConfirm] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);

  useEffect(() => {
    if (!isInstalling) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      setIsInstalling(false);
    }, 5 * 60 * 1000);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [isInstalling]);

  const handleInstall = (): void => {
    const userId = user?.id;
    if (userId === undefined || userId.length === 0) {
      toast.error('Unable to determine user ID for Slack authorization');
      return;
    }

    setIsInstalling(true);

    try {
      triggerSlackBotInstall({ orgId, userId }, () => {
        emitToolCredentialUpdated(orgId, SLACK_PROVIDER);
        setIsInstalling(false);
        toast.success('Slack bot installed successfully');
      });
    } catch {
      setIsInstalling(false);
      toast.error('Failed to open Slack authorization popup');
    }
  };

  const handleDisconnect = async (): Promise<void> => {
    setIsDisconnecting(true);
    try {
      const result = await client.toolsV2.disconnectSlackBotInstallation({});
      Assert.assertOk(result);
      emitToolCredentialUpdated(orgId, SLACK_PROVIDER);
      toast.success('Slack disconnected');
      setShowDisconnectConfirm(false);
    } catch {
      toast.error('Failed to disconnect Slack');
    } finally {
      setIsDisconnecting(false);
    }
  };

  if (user === null) {
    return (
      <p className="text-sm text-muted-foreground">
        Sign in to manage Slack connections.
      </p>
    );
  }

  return (
    <div className="space-y-2">
      <div className="rounded-lg border border-border">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead className="text-xs font-medium uppercase tracking-wide">
                Provider
              </TableHead>
              <TableHead className="text-xs font-medium uppercase tracking-wide">
                Account
              </TableHead>
              <TableHead className="text-xs font-medium uppercase tracking-wide">
                Status
              </TableHead>
              <TableHead className="w-[160px] text-xs font-medium uppercase tracking-wide text-right">
                Action
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            <TableRow>
              <TableCell>
                <div className="flex items-center gap-2">
                  <div className="flex h-6 w-6 items-center justify-center">
                    <SlackIcon />
                  </div>
                  <span className="font-medium">Slack</span>
                </div>
              </TableCell>
              <TableCell className="text-muted-foreground">
                {installed && teamId !== undefined
                  ? `Workspace ${teamId}`
                  : 'Coco bot'}
              </TableCell>
              <TableCell>
                <span
                  className={`inline-flex items-center gap-1.5 text-xs ${
                    installed ? 'text-green-600' : 'text-muted-foreground'
                  }`}
                >
                  <span
                    className={`h-1.5 w-1.5 rounded-full ${
                      installed ? 'bg-green-500' : 'bg-muted-foreground'
                    }`}
                  />
                  {installed ? 'Installed' : 'Not installed'}
                </span>
              </TableCell>
              <TableCell className="text-right">
                {installed ? (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowDisconnectConfirm((v) => !v)}
                  >
                    Disconnect
                  </Button>
                ) : (
                  <Button
                    size="sm"
                    onClick={handleInstall}
                    disabled={isInstalling}
                  >
                    {isInstalling ? (
                      <>
                        <Loading.Spinner variant="sm" />
                        Installing...
                      </>
                    ) : (
                      <>
                        Install Coco
                        <Icon icon="arrow-up-right" className="ml-1 h-3 w-3" />
                      </>
                    )}
                  </Button>
                )}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </div>
      {showDisconnectConfirm && installed && (
        <InlineDeleteConfirmation
          isPending={isDisconnecting}
          onConfirm={() => {
            void handleDisconnect();
          }}
          onCancel={() => setShowDisconnectConfirm(false)}
        />
      )}
    </div>
  );
};
